import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { fadeInUp, staggerItem } from '../utils/animations';
import { useTheme, getThemeColors } from '../contexts/ThemeContext';
import Section from './Section';
import Card from './Card';
import Button from './Button';

const Contact = () => {
  const { isDarkMode } = useTheme();
  const colors = getThemeColors(isDarkMode);
  
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    productInterest: '',
    quantity: '',
    message: '',
    urgency: 'normal'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const productOptions = [
    'Deformed Steel Bars (Rebar)',
    'Hot Rolled Flat Bars',
    'Structural Angles',
    'Steel Channels',
    'Hot Rolled Coils',
    'Custom Steel Products',
    'General Inquiry'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({
        name: '',
        company: '',
        email: '',
        phone: '',
        productInterest: '',
        quantity: '',
        message: '',
        urgency: 'normal'
      });
      
      // Reset status after 5 seconds
      setTimeout(() => setSubmitStatus(null), 5000);
    }, 2000);
  };

  const contactInfo = [
    {
      title: 'Sales Office',
      details: [
        'Industrial Area, Steel City',
        'SC 12345, Bangladesh',
        '+880 1234-567890',
        'sales@julfikarsteel.com'
      ],
      hours: 'Mon-Fri: 8:00 AM - 6:00 PM',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      )
    },
    {
      title: 'Production Facility',
      details: [
        'Steel Manufacturing Zone',
        'Industrial District, SC 12346',
        '+880 1234-567891',
        'production@julfikarsteel.com'
      ],
      hours: '24/7 Operations',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
        </svg>
      )
    },
    {
      title: 'Technical Support',
      details: [
        'Engineering Department',
        'Quality Assurance Lab',
        '+880 1234-567892',
        'technical@julfikarsteel.com'
      ],
      hours: 'Mon-Sat: 9:00 AM - 5:00 PM',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      )
    }
  ];

  return (
    <Section id="contact" background="particles" className="relative">
      {/* Section Header */}
      <motion.div
        className="text-center mb-16"
        variants={fadeInUp}
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
      >
        <h2 className={`text-h2 font-bold ${colors.text.primary} mb-6`}>
          Get in <span className="text-primary-400">Touch</span>
        </h2>
        <p className={`text-body-lg ${colors.text.secondary} max-w-3xl mx-auto leading-relaxed`}>
          Ready to discuss your steel requirements? Our technical team is here to help 
          you find the right solutions for your project.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
        {/* Contact Form */}
        <div className="lg:col-span-2">
          <motion.div
            variants={fadeInUp}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <Card glass={true} className="p-8">
              <h3 className={`text-xl font-semibold ${colors.text.primary} mb-6`}>Request a Quote</h3>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Personal Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className={`block ${colors.text.secondary} text-sm font-medium mb-2`}>
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className={`w-full px-4 py-3 ${colors.bg.muted} border ${colors.border.default} rounded-lg ${colors.text.primary} ${colors.placeholder} focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all`}
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div>
                    <label className={`block ${colors.text.secondary} text-sm font-medium mb-2`}>
                      Company
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 ${colors.bg.muted} border ${colors.border.default} rounded-lg ${colors.text.primary} ${colors.placeholder} focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all`}
                      placeholder="Company name"
                    />
                  </div>
                </div>

                {/* Contact Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className={`block ${colors.text.secondary} text-sm font-medium mb-2`}>
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className={`w-full px-4 py-3 ${colors.bg.muted} border ${colors.border.default} rounded-lg ${colors.text.primary} ${colors.placeholder} focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all`}
                      placeholder="your.email@company.com"
                    />
                  </div>
                  <div>
                    <label className={`block ${colors.text.secondary} text-sm font-medium mb-2`}>
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 ${colors.bg.muted} border ${colors.border.default} rounded-lg ${colors.text.primary} ${colors.placeholder} focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all`}
                      placeholder="+880 1234-567890"
                    />
                  </div>
                </div>

                {/* Product Interest */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className={`block ${colors.text.secondary} text-sm font-medium mb-2`}>
                      Product Interest *
                    </label>
                    <select
                      name="productInterest"
                      value={formData.productInterest}
                      onChange={handleInputChange}
                      required
                      className={`w-full px-4 py-3 ${colors.bg.muted} border ${colors.border.default} rounded-lg ${colors.text.primary} focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all`}
                    >
                      <option value="">Select a product</option>
                      {productOptions.map((product) => (
                        <option key={product} value={product}>{product}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={`block ${colors.text.secondary} text-sm font-medium mb-2`}>
                      Estimated Quantity
                    </label>
                    <input
                      type="text"
                      name="quantity"
                      value={formData.quantity}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 ${colors.bg.muted} border ${colors.border.default} rounded-lg ${colors.text.primary} ${colors.placeholder} focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all`}
                      placeholder="e.g., 100 tons, 5000 pieces"
                    />
                  </div>
                </div>

                {/* Urgency */}
                <div>
                  <label className="block text-neutral-300 text-sm font-medium mb-2">
                    Project Urgency
                  </label>
                  <div className="flex space-x-4">
                    {[
                      { value: 'urgent', label: 'Urgent (1-2 weeks)' },
                      { value: 'normal', label: 'Normal (3-4 weeks)' },
                      { value: 'flexible', label: 'Flexible (4+ weeks)' }
                    ].map((option) => (
                      <label key={option.value} className="flex items-center">
                        <input
                          type="radio"
                          name="urgency"
                          value={option.value}
                          checked={formData.urgency === option.value}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <div className={`w-4 h-4 rounded-full border-2 mr-2 ${
                          formData.urgency === option.value 
                            ? 'border-primary-400 bg-primary-400' 
                            : 'border-neutral-500'
                        }`}>
                          {formData.urgency === option.value && (
                            <div className="w-2 h-2 bg-black rounded-full m-0.5"></div>
                          )}
                        </div>
                        <span className={`${colors.text.secondary} text-sm`}>{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-neutral-300 text-sm font-medium mb-2">
                    Project Details
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    className={`w-full px-4 py-3 ${colors.bg.muted} border ${colors.border.default} rounded-lg ${colors.text.primary} ${colors.placeholder} focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all resize-none`}
                    placeholder="Please provide details about your project, specific requirements, delivery location, and any special specifications..."
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  magnetic={true}
                  disabled={isSubmitting}
                  className="w-full"
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin mr-2"></div>
                      Sending Request...
                    </div>
                  ) : (
                    'Send Quote Request'
                  )}
                </Button>

                {/* Success Message */}
                {submitStatus === 'success' && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-green-500/20 border border-green-500/30 rounded-lg p-4 text-green-400 text-center"
                  >
                    <svg className="w-6 h-6 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Thank you! Your quote request has been submitted successfully. 
                    We'll get back to you within 24 hours.
                  </motion.div>
                )}
              </form>
            </Card>
          </motion.div>
        </div>

        {/* Right Sidebar - Quick Contact and Map */}
        <div className="flex flex-col space-y-8 h-full">
          {/* Quick Contact Options */}
          <motion.div
            variants={staggerItem}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
          >
            <Card glass={true} className="p-6">
              <h3 className={`${colors.text.primary} font-semibold mb-4`}>Quick Contact</h3>
              <div className="space-y-3">
                <Button variant="ghost" size="sm" className="w-full justify-start">
                  <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  Call Sales: +880 1234-567890
                </Button>
                
                <Button variant="ghost" size="sm" className="w-full justify-start">
                  <svg className="w-4 h-4 mr-3" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                  </svg>
                  WhatsApp Chat
                </Button>
                
                <Button variant="ghost" size="sm" className="w-full justify-start">
                  <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Email: sales@julfikarsteel.com
                </Button>
              </div>
            </Card>
          </motion.div>

          {/* Map Placeholder - Extended height to match form */}
          <motion.div
            variants={staggerItem}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            className="flex-1"
          >
            <Card className="p-6 h-full relative overflow-hidden">
              <div className="absolute inset-0 steel-texture opacity-10"></div>
              <div className="relative z-10 h-full flex flex-col justify-center items-center text-center">
                <svg className="w-16 h-16 text-primary-400 mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <h4 className={`${colors.text.primary} font-semibold mb-3 text-lg`}>Visit Our Facility</h4>
                <p className={`${colors.text.secondary} text-sm mb-6 leading-relaxed`}>
                  Experience our state-of-the-art steel manufacturing facility.<br/>
                  Industrial Area, Steel City<br/>
                  SC 12345, Bangladesh
                </p>
                
                {/* Additional facility info */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center justify-center space-x-2 text-primary-400 text-xs">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Open for facility tours</span>
                  </div>
                  <div className="flex items-center justify-center space-x-2 text-primary-400 text-xs">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Quality assurance lab on-site</span>
                  </div>
                </div>
                
                <Button variant="text" size="sm" className="mt-auto">
                  Get Directions
                </Button>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>

      {/* Location Cards - Full Width Bottom Section */}
      <div className="mt-16">
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
          variants={staggerItem}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
        >
          {contactInfo.map((info, index) => (
            <motion.div
              key={info.title}
              variants={fadeInUp}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              transition={{ delay: 0.7 + (index * 0.1) }}
            >
              <Card hover={true} className="p-6 h-full">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="text-primary-400">
                    {info.icon}
                  </div>
                  <h3 className={`${colors.text.primary} font-semibold`}>{info.title}</h3>
                </div>
                
                <div className="space-y-2 mb-4">
                  {info.details.map((detail, idx) => (
                    <p key={idx} className={`${colors.text.secondary} text-sm`}>{detail}</p>
                  ))}
                </div>
                
                <p className="text-primary-400 text-sm font-medium">{info.hours}</p>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </Section>
  );
};

export default Contact;